import React from 'react';
import TrashIcon from 'part:@sanity/base/trash-icon'
import { ConfirmDeleteDialog } from '@sanity/desk-tool/lib/components/confirmDeleteDialog/ConfirmDeleteDialog';
import { IResolverProps, IUseDocumentOperationResult } from '../types';
import { getConfig, getSanityClient, getBaseIdFromId, getTranslationsFor } from '../utils';
import { useDocumentOperation } from '@sanity/react-hooks';
import { useToast } from '@sanity/ui';

/**
 * This code is mostly taken from the defualt DeleteAction provided by Sanity
 */

const DISABLED_REASON_TITLE = {
  NOTHING_TO_DELETE: "This document doesn't yet exist or is already deleted"
}

export const DeleteWithi18nAction = ({ id, type, draft, published, onComplete }: IResolverProps) => {
  const toast = useToast();
  const config = React.useMemo(() => getConfig(type), [type]);
  const baseDocumentId = React.useMemo(() => getBaseIdFromId(id), [id]);
  const { delete: deleteOp } = useDocumentOperation(id, type) as IUseDocumentOperationResult;
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const onHandle = React.useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const onDialogCancel = React.useCallback(() => {
    setConfirmDialogOpen(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  const onDialogConfirm = React.useCallback(async () => {
    try {
      setIsDeleting(true);
      setConfirmDialogOpen(false);
      const client = getSanityClient();

      const baseTransaction = client.transaction();
      baseTransaction.delete(`drafts.${baseDocumentId}`);
      baseTransaction.patch(baseDocumentId, {
        unset: [config.fieldNames.references],
      });
      await baseTransaction.commit();

      const translatedDocuments = await getTranslationsFor(baseDocumentId);
      const translationsTransaction = client.transaction();
      translatedDocuments.forEach(doc => translationsTransaction.delete(doc._id));
      await translationsTransaction.commit();

      deleteOp.execute();
      if (onComplete) onComplete();
    } catch (err) {
      toast.push({
        closable: true,
        status: 'error',
        title: err.toString(),
      });
    } finally {
      setIsDeleting(true);
    }
  }, [baseDocumentId, deleteOp, onComplete]);

  const dialogContent = React.useMemo(() => {
    if (isConfirmDialogOpen) {
      return (
        <ConfirmDeleteDialog
          action='delete'
          id={draft?._id || id}
          type={type}
          onCancel={onDialogCancel}
          onConfirm={onDialogConfirm}
        />
      )
    }
    return null;
  }, [isConfirmDialogOpen, draft, id, type, onDialogCancel, onDialogConfirm]);

  return {
    onHandle,
    color: 'danger',
    icon: TrashIcon,
    disabled: isDeleting || Boolean(deleteOp.disabled),
    title: (deleteOp.disabled && DISABLED_REASON_TITLE[deleteOp.disabled]) || '',
    label: isDeleting ? config.messages?.deleteAll?.deleting : config.messages?.deleteAll?.buttonTitle,
    dialog: isConfirmDialogOpen && {
      type: 'legacy',
      onClose: onComplete,
      title: 'Delete',
      content: dialogContent
    }
  }
}